import { useApi, useMessage } from 'app/context';
import { IPatientSchema, IPatientUpdate } from 'generated/axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from './useForm';
import { buildErrorDetails, buildErrorMessage } from 'app/utils';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';

export const usePatientDetail = (id: string) => {
  const { patientApi } = useApi();
  const { error, success } = useMessage();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<IPatientSchema>();
  const [request, setRequest] = useState<IPatientUpdate>();
  const [loading, setLoading] = useState<boolean>();

  const buildRequestFromDetail = (data: IPatientSchema): IPatientUpdate => ({
    first_name: data.first_name,
    last_name: data.last_name,
    birth_date: data.birth_date,
    company: { name: data.company?.name as string },
    tax_code: data.tax_code,
    job_title: data.job_title,
    residence: data.residence,
  });

  //get
  const get = useCallback(() => {
    setLoading(true);
    patientApi
      .webGetPatientDetails(id)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        setDetail(resp.data);
        setRequest(buildRequestFromDetail(resp.data));
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientApi, id]);

  useEffect(() => {
    get();
  }, [get]);

  //update
  const update = useCallback(
    (form: IPatientUpdate) => {
      setLoading(true);
      patientApi
        .webUpdatePatient(id, form)
        .then((resp) => {
          if (resp.status >= 400) {
            return Promise.reject(resp);
          }
          success({ message: t('patient.updateSuccess') as string });
          setDetail(resp.data);
          setRequest(buildRequestFromDetail(resp.data));
        })
        .catch((err) => {
          error({ message: buildErrorMessage(err) });
          updateForm.setErrors(buildErrorDetails(err));
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patientApi, id],
  );

  const updateForm = useForm<IPatientUpdate>({
    defaultValues: request,
    onSubmit: update,
    requiredFields: ['birth_date', 'first_name', 'last_name'],
    requiredValidationOptions: { errorMsg: t('info.mandatoryField'), validationTime: 'ON_SUBMIT' },
  });

  //delete
  const deletePatient = useCallback(() => {
    setLoading(true);
    patientApi
      .webDeletePatient(id)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        success({ message: t('patient.deleteSuccess') as string });
        navigate(-1);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientApi, id]);

  return { detail, updateForm, loading, deletePatient };
};
